import { configureChildApp } from '@rendora/common/utils/appContext';
import Message from '@rendora/design/components/message/index.vue';
import type { MessageProps } from '@rendora/design/components/message/types';
import { isMobile } from '@rendora/design/utils/responsive';

import { createApp, h, isVNode, nextTick, ref, type Component, type VNode } from 'vue';

// Message 配置接口
export interface MessageConfig extends Partial<MessageProps> {
  content?: Component | VNode | string;
  duration?: number; // 自动关闭时间，0 为不自动关闭
  onClose?: () => void;
}

// Message 实例接口
export interface MessageInstance {
  close: () => void;
  update: (config: Partial<MessageConfig>) => void;
}

// Message 工厂类
export class MessageFactory {
  private static instances = new Map<symbol, MessageInstance>();
  private static messageContainer: HTMLElement | null = null;
  private static zIndexCounter = 1000;

  static getInstance(symbol: symbol): MessageInstance {
    return MessageFactory.instances.get(symbol)!;
  }

  // 创建 Message 容器
  private static createContainer(): HTMLElement {
    if (!this.messageContainer) {
      this.messageContainer = document.createElement('div');
      this.messageContainer.id = 'message-container';
      this.messageContainer.style.position = 'fixed';
      // 使用 vw 单位替代 calc() 以提高 Android 兼容性
      // 计算公式：24 / 375 * 100vw = 6.4vw
      this.messageContainer.style.top = isMobile() ? '6.4vw' : '46px';
      this.messageContainer.style.left = '50%';
      this.messageContainer.style.transform = 'translateX(-50%)';
      this.messageContainer.style.zIndex = '9999';
      this.messageContainer.style.pointerEvents = 'none';
      this.messageContainer.style.display = 'flex';
      this.messageContainer.style.flexDirection = 'column';
      this.messageContainer.style.gap = '4px';
      this.messageContainer.style.width = 'max-content';
      this.messageContainer.style.alignItems = 'center';
      document.body.appendChild(this.messageContainer);
    }
    return this.messageContainer;
  }

  // 创建 Message
  static create(
    config: MessageConfig,
    symbol?: symbol,
    customContainer?: HTMLElement,
  ): MessageInstance {
    const messageSymbol = symbol || Symbol('message');
    const visible = ref(false);

    const currentConfig = ref<MessageConfig>({
      showIcon: true,
      duration: isMobile() ? 5000 : 3000, // 默认 3 秒后自动关闭
      ...config,
    });

    // 创建容器
    const container = customContainer ?? this.createContainer();

    // 创建单个 message 的包装器
    const messageWrapper = document.createElement('div');
    messageWrapper.style.pointerEvents = 'auto';
    messageWrapper.style.zIndex = String(++this.zIndexCounter);

    let autoCloseTimer: NodeJS.Timeout | null = null;

    const messageInstance: MessageInstance = {
      close: () => {
        visible.value = false;
        if (autoCloseTimer) {
          clearTimeout(autoCloseTimer);
        }
        setTimeout(() => {
          try {
            app.unmount();
            if (messageWrapper.parentNode) {
              messageWrapper.parentNode.removeChild(messageWrapper);
            }
            MessageFactory.instances.delete(messageSymbol);
            currentConfig.value.onClose?.();
          } catch (_error) {
            // Error while closing message
          }
        }, 200); // 等待动画完成
      },
      update: (newConfig: Partial<MessageConfig>) => {
        Object.assign(currentConfig.value, newConfig);

        // 如果更新了 duration，重新设置自动关闭
        if (newConfig.duration !== undefined) {
          if (autoCloseTimer) {
            clearTimeout(autoCloseTimer);
          }
          if (newConfig.duration > 0) {
            autoCloseTimer = setTimeout(() => {
              messageInstance.close();
            }, newConfig.duration);
          }
        }
      },
    };

    MessageFactory.instances.set(messageSymbol, messageInstance);

    // 创建 Vue 应用
    const app = createApp({
      setup() {
        const handleClose = () => {
          messageInstance.close();
        };

        // 自动关闭逻辑
        if (currentConfig.value.duration && currentConfig.value.duration > 0) {
          autoCloseTimer = setTimeout(() => {
            messageInstance.close();
          }, currentConfig.value.duration);
        }

        return () => (
          <div
            class="message-item transition-all duration-200 ease-in-out"
            style={{
              transform: visible.value ? 'translateY(0%)' : 'translateY(-100%)',
              opacity: visible.value ? '1' : '0',
            }}
          >
            <Message
              {...(currentConfig.value as MessageProps)}
              onClose={handleClose}
              v-slots={{
                title: (() => {
                  const content = currentConfig.value.content;
                  if (!content) return null;
                  if (typeof content === 'string') return content;
                  if (isVNode(content)) return content as VNode;
                  return h(content as Component);
                })(),
              }}
            />
          </div>
        );
      },
    });

    // 配置消息应用 - 继承主应用的 i18n、指令等
    configureChildApp(app);

    // 挂载应用
    container.appendChild(messageWrapper);
    app.mount(messageWrapper);

    // 显示动画
    nextTick(() => {
      visible.value = true;
    });

    return messageInstance;
  }

  // 便捷方法：创建成功消息
  static success(
    config: Omit<MessageConfig, 'variant'>,
    symbol?: symbol,
    customContainer?: HTMLElement,
  ): MessageInstance {
    return this.create({ ...config, variant: 'success' } as MessageConfig, symbol, customContainer);
  }

  // 便捷方法：创建错误消息
  static error(
    config: Omit<MessageConfig, 'variant'>,
    symbol?: symbol,
    customContainer?: HTMLElement,
  ): MessageInstance {
    return this.create({ ...config, variant: 'error' } as MessageConfig, symbol, customContainer);
  }

  // 便捷方法：创建警告消息
  static warning(
    config: Omit<MessageConfig, 'variant'>,
    symbol?: symbol,
    customContainer?: HTMLElement,
  ): MessageInstance {
    return this.create({ ...config, variant: 'warning' } as MessageConfig, symbol, customContainer);
  }

  // 便捷方法：创建信息消息
  static info(
    config: Omit<MessageConfig, 'variant'>,
    symbol?: symbol,
    customContainer?: HTMLElement,
  ): MessageInstance {
    return this.create({ ...config, variant: 'info' } as MessageConfig, symbol, customContainer);
  }

  // 清除所有 message
  static clear() {
    this.instances.forEach(instance => {
      instance.close();
    });
    this.instances.clear();

    if (this.messageContainer) {
      this.messageContainer.remove();
      this.messageContainer = null;
    }
  }

  // 清除特定类型的 message
  static clearByType(_variant: MessageProps['variant']) {
    // 这里需要在实例中保存 variant 信息才能实现
    // 当前实现中可以通过扩展来支持
  }
}
